/**
 * P2-04 · <PriceDisplay> — the one place card price logic lives (P2-DB04
 * §01): currency, compare-at vs sale emphasis, "From" ranges, and the
 * accessible was/now reading. Reused by grid and list variants and by the
 * swatch-hover preview so pricing never diverges across surfaces.
 */
import { formatMoney, isOnSale } from "~/lib/money";
import { cn } from "~/lib/utils";

export interface PriceDisplayProps {
  priceMinor: number | null;
  compareAtMinor: number | null;
  currency: string;
  /** Prefix with "From" — variant price ranges (never during a preview). */
  from?: boolean;
  className?: string;
}

export function PriceDisplay({
  priceMinor,
  compareAtMinor,
  currency,
  from,
  className,
}: PriceDisplayProps) {
  if (priceMinor === null) return null;
  const price = formatMoney(priceMinor, currency);

  if (!isOnSale(priceMinor, compareAtMinor)) {
    return (
      <span className={cn("text-sm font-semibold text-fg", className)}>
        {from ? `From ${price}` : price}
      </span>
    );
  }

  const wasPrice = formatMoney(compareAtMinor!, currency);
  return (
    <span className={cn("flex items-baseline gap-1.5", className)}>
      {/* One coherent sentence for screen readers; visuals are aria-hidden. */}
      <span className="sr-only">{`Now ${price}, was ${wasPrice}`}</span>
      <span aria-hidden className="text-sm font-semibold text-danger">
        {from ? `From ${price}` : price}
      </span>
      <s aria-hidden className="text-xs text-fg-muted">
        {wasPrice}
      </s>
    </span>
  );
}
